import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';

import { logger } from '../../../core/Log/logger';
import {
    addItineraryToWishList,
    getAllWishListItineraries,
    removeItineraryFromWishList,
} from '../../../resources/itinerary/api/pyrite/itinerary';
import { useLocale } from '../../hooks/useLocale';
import { useUser } from '../user/useUser';

type ItineraryWish = number;

export type WishListToggleCallbackType = (id: number) => void;

export type WishListable = {
    onWishListAdd?: WishListToggleCallbackType;
    onWishListRemove?: WishListToggleCallbackType;
};

interface WishList {
    itineraries: ItineraryWish[];
}

interface WishListContextValue {
    fetchAll: () => Promise<void>;
    wishList: WishList;
    history: History;
    fetched: boolean;
    addItinerary: (id: number) => void;
    removeItinerary: (id: number) => void;
}

export const WishListContext = React.createContext<WishListContextValue>({
    fetchAll: () => Promise.resolve(),
    wishList: {
        itineraries: [],
    },
    history: {
        itineraries: [],
    },
    fetched: false,
    addItinerary: () => {
        return;
    },
    removeItinerary: () => {
        return;
    },
});

type History = {
    itineraries: ItineraryWish[][];
};

export function WishListProvider(props: PropsWithChildren<unknown>) {
    const { locale } = useLocale();
    const { user } = useUser();
    const [fetched, setFetched] = useState(false);
    const [itineraries, setItineraries] = useState<ItineraryWish[]>([]);
    const [history, setHistory] = useState<History>({ itineraries: [] });

    useEffect(() => {
        if (fetched) {
            setHistory((history) => {
                return {
                    ...history,
                    itineraries: [...history.itineraries, itineraries],
                };
            });
        }
    }, [itineraries, fetched]);

    const fetchAll = useCallback(() => {
        async function fetchItinerariesWishList() {
            try {
                const itineraries = await getAllWishListItineraries({ locale });
                const itinerariesIds = itineraries.map((itinerary) => {
                    return itinerary.id;
                });

                setItineraries(itinerariesIds);
            } catch (error) {
                logger.error('Fetch WishListItineraries failed', { locale, error });
            }
        }

        setFetched(false);

        return fetchItinerariesWishList().then(() => {
            setFetched(true);
        });
    }, [locale]);

    const addItinerary = useCallback(
        (itineraryId: number) => {
            setItineraries((itineraries) => {
                return [...itineraries, itineraryId];
            });

            addItineraryToWishList({ locale, itineraryId }).catch((error) => {
                setItineraries((previousItineraries) => {
                    return previousItineraries.filter((id) => {
                        return id !== itineraryId;
                    });
                });

                logger.error('Add WishListItinerary failed', { locale, itineraryId, error });
            });
        },
        [locale],
    );

    const removeItinerary = useCallback(
        (itineraryId: number) => {
            setItineraries((previousItineraries) => {
                return previousItineraries.filter((id) => {
                    return id !== itineraryId;
                });
            });

            removeItineraryFromWishList({ locale, itineraryId }).catch((error) => {
                setItineraries((previousItineraries) => {
                    return [...previousItineraries, itineraryId];
                });

                logger.error('Remove WishListItinerary failed', { locale, itineraryId, error });
            });
        },
        [locale],
    );

    useEffect(() => {
        if (user !== null) {
            void fetchAll();
        }
    }, [fetchAll, user]);

    return (
        <WishListContext.Provider
            value={{
                fetchAll,
                wishList: { itineraries },
                history,
                fetched,
                addItinerary,
                removeItinerary,
            }}
        >
            {props.children}
        </WishListContext.Provider>
    );
}
